/**
 * FloatingInsuranceHelp.jsx
 * Global floating "Insurance Help" quick-action widget for Kaphy Insurance Agency.
 *
 * Not a chatbot — a compact launcher that opens a small panel of direct
 * links to the auto quote form, renters insurance page, and contact page.
 * Mounted once at the app root so it persists across route changes; hides
 * itself on the admin dashboard and auth pages.
 */
import React, { useState, useRef, useEffect, useCallback, useId } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import "./FloatingInsuranceHelpStyle.css";

const HELP_OPTIONS = [
  {
    id: "book-appointment",
    icon: "fa-calendar-check",
    title: "Book an Appointment",
    description: "Schedule a meeting with a licensed agent",
    path: "/appointments",
  },
  {
    id: "auto-quote",
    icon: "fa-car-side",
    title: "Get an Auto Quote",
    description: "Start your auto insurance quote",
    path: "/auto-quote-form",
  },
  {
    id: "renters-insurance",
    icon: "fa-key",
    title: "Renters Insurance",
    description: "Explore renters insurance coverage",
    path: "/renters-insurance",
  },
  {
    id: "general-liability",
    icon: "fa-briefcase",
    title: "Errors & Omissions / E&O",
    description: "Get an E&O insurance quote",
    path: "/get-general-liability",
  },
  {
    id: "business-liability",
    icon: "fa-building-shield",
    title: "General Business Liability",
    description: "Request coverage for business liability exposures",
    path: "/get-business-liability-quote",
  },
  {
    id: "truck-insurance",
    icon: "fa-truck",
    title: "Commercial Truck Insurance",
    description: "Get a quote for your Minnesota trucking operation",
    path: "/commercial-truck-insurance-quote",
  },
  {
    id: "contact-us",
    icon: "fa-phone",
    title: "Contact Our Agency",
    description: "Contact Kaphy Insurance Agency",
    path: "/contact-us",
  },
];

// Widget stays hidden on the admin dashboard and authentication pages
const HIDDEN_PATH_PREFIXES = ["/dashboard", "/api/auth"];

// sessionStorage key — set when the attention sequence starts so it never
// repeats within the same browser session, even across rapid navigations.
const ATTENTION_KEY = "fih_attention_shown";

/**
 * Attempts to play a soft two-note chime via the Web Audio API.
 *
 * Modern browsers suspend new AudioContexts until a user gesture, so this
 * will silently no-op in most cases on first page load — the animation still
 * runs as the primary attention mechanism. If the context is already running
 * (rare on first visit but possible in some browsers/extensions), a gentle
 * 880 Hz + 1109 Hz chime plays at very low volume (0.07 / 1.0).
 *
 * Respects prefers-reduced-motion: callers that respect this preference are
 * expected to skip calling this function (see the attention useEffect below).
 */
function playAttentionChime() {
  try {
    const Ctx = window.AudioContext || window["webkitAudioContext"];
    if (!Ctx) return;
    const ctx = new Ctx();
    // Bail if the browser has suspended the context (autoplay policy).
    if (ctx.state === "suspended") {
      ctx.close();
      return;
    }
    const master = ctx.createGain();
    master.gain.value = 0.07;
    master.connect(ctx.destination);
    // A5 (880 Hz) followed by C#6 (≈1109 Hz) — a brief ascending chime
    [[880, 0], [1108.73, 0.15]].forEach(([freq, offset]) => {
      const osc = ctx.createOscillator();
      const env = ctx.createGain();
      osc.connect(env);
      env.connect(master);
      osc.type = "sine";
      osc.frequency.value = freq;
      const t = ctx.currentTime + offset;
      env.gain.setValueAtTime(0, t);
      env.gain.linearRampToValueAtTime(1, t + 0.01);
      env.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
      osc.start(t);
      osc.stop(t + 0.4);
    });
    setTimeout(() => ctx.close(), 1200);
  } catch {
    // Autoplay blocked or AudioContext unavailable — animation continues
  }
}

const FloatingInsuranceHelp = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  // The cookie-consent banner (see CookieConsent.js) occupies a full-width
  // strip at the bottom of the screen for first-time visitors and claims
  // pointer events across its full width, not just its centered card. Yield
  // to it (read-only DOM observation, no changes to CookieConsent itself)
  // so the launcher isn't hidden underneath / visually collides with it.
  const [yieldToCookieBanner, setYieldToCookieBanner] = useState(false);
  // Attention-animation state — bounce class is applied once per session
  const [attentionActive, setAttentionActive] = useState(false);
  // Attention tooltip ("Need insurance help?") — auto-dismissed after 4.5 s
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const panelRef = useRef(null);
  const launcherRef = useRef(null);
  const closeBtnRef = useRef(null);
  const headingId = useId();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();

  const shouldHide =
    yieldToCookieBanner ||
    (!loading && !!user) ||
    HIDDEN_PATH_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));

  const closePanel = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      setIsOpen(false);
      launcherRef.current?.focus();
    }, 220);
  }, []);

  const openPanel = useCallback(() => {
    setIsOpen(true);
    setTooltipVisible(false); // dismiss attention tooltip when panel opens
    setTimeout(() => setIsVisible(true), 20);
  }, []);

  const togglePanel = () => {
    if (isOpen) {
      closePanel();
    } else {
      openPanel();
    }
  };

  // ─── Attention sequence ──────────────────────────────────────────────────
  // Fires once per session on the homepage after a 2.5 s delay.
  // sessionStorage is set immediately (before the timer fires) so that rapid
  // navigation away from "/" before the 2.5 s elapses cannot re-schedule it.
  useEffect(() => {
    if (loading || shouldHide || location.pathname !== "/") return;
    if (sessionStorage.getItem(ATTENTION_KEY)) return;

    sessionStorage.setItem(ATTENTION_KEY, "1");

    const timer = setTimeout(() => {
      setTooltipVisible(true);
      setAttentionActive(true);
      // Only attempt sound if the user hasn't opted out of motion effects;
      // the chime relies on the same sensory channel as the animation.
      if (!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
        playAttentionChime();
      }
    }, 2500);

    return () => clearTimeout(timer);
  }, [loading, shouldHide, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Remove the bounce class once the CSS animation finishes (1.4 s)
  useEffect(() => {
    if (!attentionActive) return;
    const t = setTimeout(() => setAttentionActive(false), 1400);
    return () => clearTimeout(t);
  }, [attentionActive]);

  // Auto-dismiss the attention tooltip after 4.5 s
  useEffect(() => {
    if (!tooltipVisible) return;
    const t = setTimeout(() => setTooltipVisible(false), 4500);
    return () => clearTimeout(t);
  }, [tooltipVisible]);
  // ─────────────────────────────────────────────────────────────────────────

  // Watch the cookie-consent banner's visible class and yield while it's shown
  useEffect(() => {
    let classObserver;
    const attachClassObserver = (bannerEl) => {
      const update = () =>
        setYieldToCookieBanner(
          bannerEl.classList.contains("kaphy-cookie-banner--visible"),
        );
      update();
      classObserver = new MutationObserver(update);
      classObserver.observe(bannerEl, {
        attributes: true,
        attributeFilter: ["class"],
      });
    };

    const existingBanner = document.querySelector(".kaphy-cookie-banner");
    if (existingBanner) attachClassObserver(existingBanner);

    const bodyObserver = new MutationObserver(() => {
      if (classObserver) return;
      const bannerEl = document.querySelector(".kaphy-cookie-banner");
      if (bannerEl) attachClassObserver(bannerEl);
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      bodyObserver.disconnect();
      classObserver?.disconnect();
    };
  }, []);

  // Force-close the panel if the cookie banner appears while it's open
  useEffect(() => {
    if (yieldToCookieBanner) {
      setIsVisible(false);
      setIsOpen(false);
    }
  }, [yieldToCookieBanner]);

  // Focus the close button once the panel mounts
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => closeBtnRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [isOpen]);

  // Escape closes the panel; Tab is trapped inside it while open
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        closePanel();
        return;
      }
      if (e.key === "Tab" && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, closePanel]);

  // Click/tap outside the panel and launcher closes it
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        launcherRef.current &&
        !launcherRef.current.contains(e.target)
      ) {
        closePanel();
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [isOpen, closePanel]);

  // Close the panel and clear the attention tooltip on navigation
  useEffect(() => {
    setIsVisible(false);
    setIsOpen(false);
    setTooltipVisible(false);
    setAttentionActive(false);
  }, [location.pathname]);

  const handleOptionClick = (path) => {
    setIsVisible(false);
    setIsOpen(false);
    navigate(path);
  };

  if (shouldHide) return null;

  return (
    <>
      {isOpen && (
        <div
          ref={panelRef}
          id="fih-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby={headingId}
          className={`fih-panel${isVisible ? " fih-panel--visible" : ""}`}
        >
          <div className="fih-panel__header">
            <h2 id={headingId} className="fih-panel__title">
              How can we help you today?
            </h2>
            <button
              ref={closeBtnRef}
              type="button"
              className="fih-panel__close"
              onClick={closePanel}
              aria-label="Close help panel"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
          </div>

          <div className="fih-panel__options" role="list">
            {HELP_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                role="listitem"
                className="fih-option"
                onClick={() => handleOptionClick(opt.path)}
              >
                <span className="fih-option__icon" aria-hidden="true">
                  <i className={`fa-solid ${opt.icon}`}></i>
                </span>
                <span className="fih-option__text">
                  <span className="fih-option__title">{opt.title}</span>
                  <span className="fih-option__desc">{opt.description}</span>
                </span>
                <i
                  className="fa-solid fa-chevron-right fih-option__chevron"
                  aria-hidden="true"
                ></i>
              </button>
            ))}
          </div>
        </div>
      )}

      {/*
        Attention tooltip — always in the DOM so the aria-live region is
        established before the content appears. Text is present so screen
        readers receive the polite announcement when it becomes visible.
        aria-hidden keeps it silent until the attention sequence fires.
      */}
      <div
        className={`fih-tooltip${tooltipVisible ? " fih-tooltip--visible" : ""}`}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        aria-hidden={tooltipVisible ? undefined : true}
      >
        <i
          className="fa-solid fa-shield-halved fih-tooltip__icon"
          aria-hidden="true"
        ></i>
        Need insurance help?
      </div>

      <button
        ref={launcherRef}
        type="button"
        className={`fih-launcher${isOpen ? " fih-launcher--active" : ""}${attentionActive ? " fih-launcher--attention" : ""}`}
        onClick={togglePanel}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls="fih-panel"
        aria-label={
          isOpen ? "Close insurance help menu" : "Open insurance help menu"
        }
      >
        <i
          className={`fa-solid ${isOpen ? "fa-xmark" : "fa-life-ring"} fih-launcher__icon`}
          aria-hidden="true"
        ></i>
        <span className="fih-launcher__label">
          {isOpen ? "Close" : "Insurance Help"}
        </span>
      </button>
    </>
  );
};

export default FloatingInsuranceHelp;
